import type { IResponseResult, IRequestOptions } from 'models/api.model'
import type {
  ICreateLeadRequest,
  ILeadRaw,
  ICreateCohortLeadRequest,
  IUpdateLeadRequest,
  IGetFirebaseCustomTokenResponse,
} from 'models/lead.model'
import type { IPlansResponseRaw } from 'models/plan.model'
import type {
  IPredictedLtvResponse,
  IPurchaseRequest,
  IPurchaseResponseRaw,
} from 'models/purchase.model'
import { RequestMethod } from 'root-constants'
import { ApiService } from 'services/api.service'

export class SubscriptionsApi {
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  createLead(body: ICreateLeadRequest): Promise<IResponseResult<ILeadRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: body as Record<string, any>,
    }

    return this.api.makeRequest<ILeadRaw>('api/v1/leads', options)
  }

  createCohortLead(
    body: ICreateCohortLeadRequest,
  ): Promise<IResponseResult<ILeadRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: body as Record<string, any>,
    }

    return this.api.makeRequest<ILeadRaw>('api/v1/cohort-leads', options)
  }

  updateLead(
    leadId: string,
    body: IUpdateLeadRequest,
    { keepalive }: { keepalive?: boolean } = {},
  ): Promise<IResponseResult<ILeadRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.PUT,
      body: body as Record<string, any>,
      keepalive,
    }

    return this.api.makeRequest<ILeadRaw>(`api/v1/leads/${leadId}`, options)
  }

  updateQuiz(
    leadId: string,
    updates: Record<string, any>,
  ): Promise<IResponseResult<unknown>> {
    const options: IRequestOptions = {
      method: RequestMethod.PUT,
      body: {
        quizzes: updates,
      },
    }

    return this.api.makeRequest<unknown>(
      `api/v1/leads/${leadId}/quizzes`,
      options,
    )
  }

  getLead({
    leadId,
    cohort,
  }: {
    leadId: string
    cohort: string
  }): Promise<IResponseResult<ILeadRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<ILeadRaw>(
      `api/v1/leads/${leadId}?cohort=${cohort}`,
      options,
    )
  }

  getPlans(cohortLeadId: string): Promise<IResponseResult<IPlansResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IPlansResponseRaw>(
      `api/v1/cohort-leads/${cohortLeadId}/plans`,
      options,
    )
  }

  getFirebaseCustomToken({
    leadId,
    firebaseToken,
  }: {
    leadId: string
    firebaseToken: string
  }): Promise<IResponseResult<IGetFirebaseCustomTokenResponse>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      params: {
        token: firebaseToken,
        id: leadId,
      },
    }

    return this.api.makeRequest<IGetFirebaseCustomTokenResponse>(
      `api/v1/leads/${leadId}/custom-token`,
      options,
    )
  }

  getPredictedLtv({
    cohortLeadId,
  }: {
    cohortLeadId: string
  }): Promise<IResponseResult<IPredictedLtvResponse>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
    }

    return this.api.makeRequest<IPredictedLtvResponse>(
      `api/v1/ltv?cohortLeadId=${cohortLeadId}`,
      options,
    )
  }

  purchasePlan(
    data: IPurchaseRequest,
  ): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: data as Record<string, any>,
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      'api/v1/purchases',
      options,
    )
  }

  getPurchasedPlan({
    id,
    polling = true,
    ignoreRequiresAction = false,
  }: {
    id: string
    polling?: boolean
    ignoreRequiresAction?: boolean
  }): Promise<IResponseResult<IPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.GET,
      params: {
        ...(polling && { polling }),
        ...(ignoreRequiresAction && { ignoreRequiresAction }),
      },
    }

    return this.api.makeRequest<IPurchaseResponseRaw>(
      `api/v1/purchases/${id}`,
      options,
    )
  }
}
